import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Github, Linkedin, Twitter, Mail } from "lucide-react";
import { toast } from "sonner";
import { NAV } from "@/lib/content";

const SERVICE_LINKS = [
  { label: "Custom Software", href: "/services" },
  { label: "Web & Mobile", href: "/services" },
  { label: "Cloud & DevOps", href: "/services" },
  { label: "AI & ML", href: "/services" },
  { label: "UI/UX Design", href: "/services" },
  { label: "Consulting", href: "/services" },
];

const COMPANY_EXTRA = [
  { label: "Careers", href: "/careers" },
  { label: "Legal", href: "/legal" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    await new Promise((res) => setTimeout(res, 400));
    toast.success("You're in. We'll send something worth reading.");
    setEmail("");
    setLoading(false);
  };

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/[0.06] mt-32">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-nexus-cyan/40 to-transparent" />
      <div className="mx-auto max-w-7xl px-6 md:px-12 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-full overflow-hidden border border-white/20 bg-white flex items-center justify-center shrink-0">
                <img src="/company.jpg" alt="Fullstack Endeavours" className="w-full h-full object-contain" />
              </div>
              <span className="font-display text-2xl">Fullstack Endeavours</span>
            </Link>
            <p className="text-zinc-400 max-w-md text-base leading-relaxed">
              We design and build software that scales — for startups in their first year and
              enterprises into their next decade.
            </p>
            <form onSubmit={subscribe} data-testid="newsletter-form" className="max-w-md">
              <div className="flex items-center gap-2 rounded-full glass border-white/10 pl-5 pr-1.5 py-1.5">
                <Mail className="h-4 w-4 text-zinc-500 shrink-0" />
                <input
                  data-testid="newsletter-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your work email"
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-zinc-600"
                />
                <button
                  data-testid="newsletter-submit"
                  type="submit"
                  disabled={loading}
                  className="rounded-full bg-nexus-cyan text-nexus-ink font-semibold text-xs px-4 py-2 hover:brightness-110 disabled:opacity-60"
                >
                  {loading ? "..." : "Subscribe"}
                </button>
              </div>
              <p className="text-xs text-zinc-500 mt-3">
                Monthly notes on engineering, design, and what we're shipping. No spam.
              </p>
            </form>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-10">
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-nexus-cyan font-bold mb-5">Company</h4>
              <ul className="space-y-3">
                {NAV.map((n) => (
                  <li key={n.href}>
                    <Link
                      to={n.href}
                      data-testid={`footer-link-${n.label.toLowerCase()}`}
                      className="text-sm text-zinc-400 hover:text-foreground transition"
                    >
                      {n.label}
                    </Link>
                  </li>
                ))}
                {COMPANY_EXTRA.map((c) => (
                  <li key={c.href}>
                    <Link
                      to={c.href}
                      data-testid={`footer-link-${c.label.toLowerCase()}`}
                      className="text-sm text-zinc-400 hover:text-foreground transition"
                    >
                      {c.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-nexus-cyan font-bold mb-5">Services</h4>
              <ul className="space-y-3">
                {SERVICE_LINKS.map((s) => (
                  <li key={s.label}>
                    <Link to={s.href} className="text-sm text-zinc-400 hover:text-foreground transition">
                      {s.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] text-nexus-cyan font-bold mb-5">Contact</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="mailto:admin@fullstackendeavours.in" className="text-zinc-400 hover:text-foreground inline-flex items-center gap-1.5 link-underline">
                    admin@fullstackendeavours.in <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </li>
                <li>
                  <a href="tel:+15551234567" className="text-zinc-400 hover:text-foreground">
                    +1 (555) 123-4567
                  </a>
                </li>
                <li className="text-zinc-500">228 Hudson Street, NY 10013</li>
              </ul>
              <div className="flex items-center gap-2 mt-5">
                {[
                  { Icon: Twitter, href: "https://twitter.com", label: "Twitter" },
                  { Icon: Github, href: "https://github.com", label: "GitHub" },
                  { Icon: Linkedin, href: "https://linkedin.com", label: "LinkedIn" },
                ].map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    data-testid={`social-${label.toLowerCase()}`}
                    aria-label={label}
                    target="_blank"
                    rel="noreferrer"
                    className="grid place-items-center h-9 w-9 rounded-full border border-white/10 text-zinc-400 hover:text-foreground hover:bg-white/[0.05] transition"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
                <a
                  href="https://www.instagram.com/fullstack.endeavours/"
                  data-testid="social-instagram"
                  aria-label="Instagram"
                  target="_blank"
                  rel="noreferrer"
                  className="grid place-items-center h-9 w-9 rounded-full border border-white/10 text-zinc-400 hover:text-foreground hover:bg-white/[0.05] transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/[0.06] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-500">© {new Date().getFullYear()} Fullstack Endeavours. All rights reserved.</p>
          <p className="font-mono text-[10px] tracking-widest text-zinc-600 uppercase">
            Made in NYC • Shipping worldwide
          </p>
        </div>
      </div>
    </footer>
  );
}
